import { StatusBadge } from '@argink/ui';

/**
 * A data é calculada na renderização, não cravada no código: uma página pública
 * com "30 de agosto de 2026" vira, em três meses, um comprovante visivelmente
 * velho exibido como se fosse a tela viva.
 */
function assinadoEm(now = new Date()): { legivel: string; iso: string } {
  const quando = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000);
  quando.setHours(9, 41, 7, 0);
  return {
    legivel: `${quando.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })}, 09:41 (horário de Brasília)`,
    iso: quando.toISOString().replace(/\.\d{3}Z$/, 'Z'),
  };
}

/** O que a pessoa comum precisa saber, na ordem em que ela pergunta. */
function resumo(quando: string) {
  return [
    { label: 'Quem', value: 'Marina Alves · marina@empresa.com.br' },
    { label: 'Quando', value: quando },
    { label: 'Documento', value: 'Contrato de prestação de serviços · 4 páginas' },
    { label: 'Método', value: 'Assinatura eletrônica' },
  ];
}

/**
 * A trilha de evidências com profundidade progressiva (§14 do prompt de M3).
 *
 * Nenhum título aqui é `<h1>`–`<h6>`: a representação vive DENTRO da página e
 * não é seção dela. Como heading, ela criava salto de nível no outline (h1 do
 * hero direto para h3) — violação de `heading-order` pega pelo axe. O nome
 * acessível da figura quem dá é a legenda do `ProductFrame`.
 *
 * A regra de composição é a ordem: primeiro **quem, quando, o quê, como** — em
 * português, legível por qualquer pessoa. O detalhe técnico existe, é real e
 * importa, mas fica atrás de um clique, porque despejá-lo na superfície
 * transformaria confiança em intimidação.
 *
 * `<details>` nativo de propósito: teclado, leitor de tela e estado aberto/fechado
 * saem prontos e corretos, sem componente novo e sem ARIA escrito à mão.
 */
export interface EvidenceTrailPreviewProps {
  /** Versão reduzida, para flutuar sobre a janela do app no hero: só o resumo,
   * sem o disclosure técnico, que ali competiria com a tela por trás. */
  compact?: boolean;
}

export function EvidenceTrailPreview({ compact = false }: EvidenceTrailPreviewProps) {
  const { legivel, iso } = assinadoEm();
  const SUMMARY = resumo(legivel);

  return (
    <div className={compact ? 'evidence-trail evidence-trail--compact' : 'evidence-trail'}>
      <header className="evidence-trail__header">
        <p className="evidence-trail__title">Comprovante de assinatura</p>
        <StatusBadge tone="proof">Verificado</StatusBadge>
      </header>

      <dl className="evidence-trail__summary">
        {/* No modo compacto a peça flutua sobre a janela do app e concorre com
          * ela: o resumo cai para as três linhas que respondem "quem, quando,
          * como", sem o e-mail longo que quebrava em três linhas. */}
        {(compact ? SUMMARY.filter((row) => row.label !== 'Documento').map((row) =>
              row.label === 'Quem' ? { ...row, value: 'Marina Alves' } : row,
            )
          : SUMMARY
        ).map((row) => (
          <div key={row.label} className="evidence-trail__row">
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>

      {compact ? null : (
      <details className="evidence-trail__details">
        <summary>Ver detalhes técnicos</summary>
        <dl className="evidence-trail__technical">
          <div className="evidence-trail__row">
            <dt>Identificador do documento</dt>
            <dd className="mono">a3f1c9…8e42</dd>
          </div>
          <div className="evidence-trail__row">
            <dt>Carimbo de tempo</dt>
            <dd className="mono">{iso}</dd>
          </div>
          <div className="evidence-trail__row">
            <dt>Pacote de evidências</dt>
            <dd>Disponível para download e verificação</dd>
          </div>
        </dl>
      </details>
      )}
    </div>
  );
}
